import { ArrowDown } from '@ui/components/icons';
import { filterAnyCollateralPayableTokens } from '@ui/config/brand';
import { useBrand } from '@ui/config/BrandProvider';
import { getPayablePoolTokens } from '@ui/constants/tokens';
import { useDataChainId } from '@ui/hooks/datachain';
import { Button } from '@ui/lib/park/ui/button';
import { Icon } from '@ui/lib/park/ui/icon';
import { Text } from '@ui/lib/park/ui/text';
import { PayablePoolToken } from '@ui/types/token';
import { useTranslation } from 'next-i18next';
import { useMemo } from 'react';
import { HStack, styled } from 'styled-system/jsx';

import { useSwapTokenCtaPopoverStore } from '../../../../components/DexSwap/hooks/useSwapTokenCtaPopoverStore';
import { CollateralTokenPicker } from '../CollateralTokenPicker/CollateralTokenPicker';
import { CollateralInputProps } from './types';

export const CollateralToken = ({
  setToken,
  token,
  overridePayableTokens,
}: Pick<CollateralInputProps, 'setToken' | 'token'> & {
  overridePayableTokens?: PayablePoolToken[];
}) => {
  const { t } = useTranslation();
  const brand = useBrand();
  const setOpen = useSwapTokenCtaPopoverStore((s) => s.setOpen);
  const chainId = useDataChainId();
  const payableTokens = useMemo(() => {
    const allPayableTokens =
      overridePayableTokens ?? getPayablePoolTokens(chainId);

    return filterAnyCollateralPayableTokens(allPayableTokens, brand);
  }, [brand, chainId, overridePayableTokens]);

  if (setToken && payableTokens.length > 1) {
    return (
      <CollateralTokenPicker
        overridePayableTokens={payableTokens}
        onSelect={setToken}
        title={t('common.Pick Collateral Token')}
      >
        <Button
          bg="v2.bg.surface2"
          boxShadow="none"
          color="v2.text.primary"
          fontWeight={600}
          px="10px"
          h="26px"
          minW="82px"
          gap="6px"
          variant="primary"
          borderRadius="2px"
          _hover={{
            bg: 'v2.bg.hover',
          }}
          onClick={() => setOpen(false)}
          data-cy="TokenPicker"
        >
          <styled.img
            src={token?.image}
            alt="token icon"
            width="16px"
            height="16px"
          />
          <Text fontSize="12px" fontWeight={500}>
            {token?.displaySymbol || '---'}
          </Text>
          <Icon boxSize="14px" color="v2.text.muted">
            <ArrowDown />
          </Icon>
        </Button>
      </CollateralTokenPicker>
    );
  }

  return (
    <HStack
      alignItems="center"
      gap="6px"
      flexShrink={0}
      h="34px"
      minW="82px"
      px="12px"
      justifyContent="center"
      data-cy="CollateralTokenReadOnly"
    >
      <styled.img
        src={token?.image}
        alt="token icon"
        width="16px"
        height="16px"
        flexShrink={0}
      />
      <Text fontSize="12px" fontWeight={500} whiteSpace="nowrap">
        {token?.displaySymbol || '---'}
      </Text>
    </HStack>
  );
};
